import React from 'react';
import withFunctionCall from "@mpc/common/src/utils/withFunctionCall";
import { Button, Form, Message, Modal, Space } from '@bedrock/components';
import DescriptionsFormItem from '@mpc/common/src/components/DescriptionsFormItem';
import { outsourceNewSupplierUpdateVmdmBankAccount, supplierControllerNewUpdateVmdmBankAccount } from '#services/api';
import { useDemandOptions } from "@mpc/common/src/features/useDemandOptions";
import { $sType, localPx2rem } from '#utils/index';

interface Props {
  mdm_code?: string;
  visible: boolean;
  onOk?: () => void;
  updateOptions?: (bank_business_code: string) => void;
}

const getNewBankAccountName = (text) => {
  // 含有中文则去除所有空格
  if (/[\u4e00-\u9fa5]/.test(text)) {
    return text?.replace(/\s/g, '')
  }
  // 不含有中文只去除前后空格
  return text?.trim()
}

const getNewValues = (values: any) => {
  return {
    ...values,
    bank_account: values?.bank_account?.replace(/\s/g, ''),
    swift_code: values?.swift_code?.replace(/\s/g, ''),
    // bank_account_name: getNewBankAccountName(values?.bank_account_name),
  }
}

// 当银行账户/IBAN字段填入的前两位是字母且不等于“NR、OS、FT”，此时swift code必填
const isFirstTwoLetters = (str: string): boolean => {
  if (str?.length < 2) {
    return false
  }
  const firstTwoLetters = str?.slice(0, 2);
  const regex = /^[a-zA-Z]{2}/;
  return regex.test(firstTwoLetters) && !['NR','OS','FT'].includes(firstTwoLetters);
}

// 当国家非中国（港澳台必填）且非俄罗斯时必填
const requiredByCnOrRu = (str: string): boolean => {
  return !['CN', 'RU'].includes(str)
}

const AddBankModal = ({
  visible,
  mdm_code,
  onOk,
  updateOptions = (bank_business_code: string) => {},
  ...restProps
}: Props) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = React.useState({});
  const { optionsMap } = useDemandOptions({
    keys: ['country'],
  });

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const country_name = optionsMap?.country?.options?.find(v => v.value === values?.bank_country_code)?.name;
      const api = $sType === 'inner' ? supplierControllerNewUpdateVmdmBankAccount : outsourceNewSupplierUpdateVmdmBankAccount;
      const res = await api({
        mdm_code: mdm_code,
        country_name,
        account: values?.bank_account,
        account_name: getNewBankAccountName(values?.bank_account_name),
        country_code: values?.bank_country_code,
        branch_name: values?.bank_branch_name,
        city: values?.bank_city,
        name: values?.bank_name,
        province: values?.bank_province,
        swift_code: values?.swift_code,
      });
      onOk();
      updateOptions(res?.data?.bank_business_code);
      Message.success('添加成功');
    } catch (error) {
      console.log('error', error);
    }
  }

  const onValuesChange = (changedValues, values) => {
    if (changedValues?.bank_country_code && changedValues?.bank_country_code !== 'CN') {
      const newValues = {
        ...values,
        bank_province: changedValues?.bank_country_code,
        bank_city: changedValues?.bank_country_code,
      }
      setFormData(newValues);
      form.setFieldsValue(newValues);
      return;
    }
    if (changedValues?.bank_country_code && changedValues?.bank_country_code === 'CN') {
      const newValues = {
        ...values,
        bank_province: undefined,
        bank_city: undefined,
      }
      setFormData(newValues);
      form.setFieldsValue(newValues);
      return;
    }
    setFormData(getNewValues(values));
    form.setFieldsValue(getNewValues(values));
  }

  return (
    <Modal
      width={1000}
      title='添加银行账户信息'
      visible={visible}
      {...restProps}
      onOk={handleOk}
    >
      <Form
        form={form}
        onValuesChange={onValuesChange}
      >
        <DescriptionsFormItem
          items={[
            {
              formItemKey: 'bank_account',
              label: '银行账号/IBAN',
              componentClass: 'input',
              required: true,
              span: 1,
              rules: [
                {
                  validator: (rule, value, callback) => {
                    if (/&/.test(value)) {
                      return Promise.reject('账户名称中不能包含&，请使用and');
                    };
                    if (/（|）/.test(value)) {
                      return Promise.reject('请使用英文括号');
                    }
                    return Promise.resolve();
                  },
                }
              ]
            },
            {
              formItemKey: 'bank_country_code',
              label: '开户行国家/地区',
              componentClass: 'select',
              required: true,
              span: 1,
              elementProps(params) {
                return {
                  options: optionsMap?.country?.options
                }
              },
            },
            {
              formItemKey: 'bank_account_name',
              label: '账户名称',
              componentClass: 'input',
              required: true,
              span: 1,
              rules: [
                {
                  validator: (rule, value, callback) => {
                    if (/&/.test(value)) {
                      return Promise.reject('账户名称中不能包含&，请使用and');
                    };
                    if (/（|）/.test(value)) {
                      return Promise.reject('请使用英文括号');
                    }
                    return Promise.resolve();
                  },
                }
              ]
            },
            {
              formItemKey: 'bank_name',
              label: '银行名称',
              componentClass: 'input',
              required: true,
              span: 1,
            },
            {
              formItemKey: 'bank_branch_name',
              label: '支行名称',
              componentClass: 'input',
              required: true,
              span: 1,
            },
            {
              formItemKey: 'bank_province',
              label: '开户行省',
              componentClass: 'input',
              required: true,
              span: 1,
            },
            {
              formItemKey: 'bank_city',
              label: '开户行市',
              componentClass: 'input',
              required: true,
              span: 1,
            },
            {
              formItemKey: 'swift_code',
              label: 'Swift Code',
              componentClass: 'input',
              required: requiredByCnOrRu(formData?.bank_country_code) || isFirstTwoLetters(formData?.bank_account),
              span: 2,
              rules: [
                {
                  validator: (rule, value, callback) => {
                    if (value && ![8, 11].includes(value?.length)) {
                      return Promise.reject('请提供8位或11位的编码');
                    };
                    return Promise.resolve();
                  },
                }
              ],
              inputExtra: <Space align='center' size={4}>
                <span>银行国际代码为 8位或 11位字符，可使用 </span>
                <Button type='link' onClick={() => window.open('https://www.swiftref.com/en/bicsearch')} style={{fontSize: localPx2rem(12)}}>官方查询网址</Button>
                <span>或使用 POPO的 Chatgpt查询 (结果仅供参考)</span>
              </Space>,
            },
          ]}
        />
      </Form>
    </Modal>
  )
}

export default withFunctionCall(AddBankModal);
